// src/components/tables/SteeringClocksTable.tsx
import React, { useEffect, useState } from 'react';
import { Table } from 'antd';
import ResponsiveTable from '../common/ResponsiveTable';
import type { ColumnsType } from 'antd/es/table';
import { SteeringClockItem } from '../../types/ProductItems';
import { getEditDeleteColumns } from '../../utils/columnsHelpers';
import ProductStatusDot from '../common/ProductStatusDot';

interface SteeringClocksTableProps {
  data: SteeringClockItem[];
  onEdit: (item: SteeringClockItem) => void;
  onDelete: (key: string) => void;
}

const SteeringClocksTable: React.FC<SteeringClocksTableProps> = ({ data, onEdit, onDelete }) => {
  const columns: ColumnsType<SteeringClockItem> = [
    {
      title: 'Бренд',
      dataIndex: 'brand',
      key: 'brand',
      // Закреплена слева: при прокрутке вбок бренд остаётся на экране.
      // Без этого строка теряла опознавательный признак — было видно
      // «шестигранник · 16мм · шайба», а какой товар, непонятно.
      fixed: 'left' as const,
      sorter: (a, b) => a.brand.localeCompare(b.brand),
      render: (brand: string, record) => (
        <span style={{ display: 'flex', alignItems: 'center' }}>
          <ProductStatusDot item={record} />
          {brand}
        </span>
      ),
    },
    {
      title: 'Номер детали',
      dataIndex: 'partNumber',
      key: 'partNumber',

      sorter: (a, b) => a.partNumber.localeCompare(b.partNumber),
    },
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
      sorter: (a, b) => (a.sku ?? '').localeCompare(b.sku ?? ''),
      width: 90,
      render: (val: string) => val || '—',
    },
    {
      title: 'Модель',
      dataIndex: 'model',
      key: 'model',
      
      sorter: (a, b) => a.model.localeCompare(b.model),
    },
    {
      title: 'Количество разъёмов',
      dataIndex: 'connectorCount',
      key: 'connectorCount',
     
      sorter: (a, b) => a.connectorCount - b.connectorCount,
    },
    {
      title: 'Провода (для Подушки Безопасности)',
      dataIndex: 'airbagWireCount',
      key: 'airbagWireCount',
      
      sorter: (a, b) => a.airbagWireCount - b.airbagWireCount,
    },
    {
      title: 'Количество',
      dataIndex: 'quantity',
      key: 'quantity',
      
      sorter: (a, b) => a.quantity - b.quantity,
    },
    {
      title: 'Тип товара',
      dataIndex: 'original',
      key: 'original',
      filters: [
        { text: 'Оригинал', value: 'original' },
        { text: 'Копия', value: 'copy' },
      ],
      
      onFilter: (value, record) => record.original === value,
      render: (val: string) => (val === 'original' ? 'Оригинал' : 'Копия'),
      sorter: (a, b) => a.original.localeCompare(b.original),
    },
    {
      title: 'Цена',
      dataIndex: 'price',
      key: 'price',
      sorter: (a, b) => a.price - b.price,
      render: (price: number) => `${price} ֏`,
    },
    ...getEditDeleteColumns<SteeringClockItem>(onEdit, onDelete),
  ];

const [highlightId, setHighlightId] = useState<string | null>(() =>
  localStorage.getItem('highlightProductId')
);

// --- пагинация: контролируемый режим + запоминание выбора
const [pageSize, setPageSize] = useState<number>(() => {
  const saved = Number(localStorage.getItem('steeringclocks_pageSize'));
  return Number.isFinite(saved) && saved > 0 ? saved : 10;
});
const [current, setCurrent] = useState<number>(1);

// Когда данные загружаются — переходим на страницу с выделенным товаром
useEffect(() => {
  if (!highlightId || !data.length) return;
  const idx = data.findIndex((item) => item.id === highlightId);
  if (idx !== -1) setCurrent(Math.floor(idx / pageSize) + 1);
}, [highlightId, data.length, pageSize]);

// если после фильтрации/обновления данных текущая страница стала невалидной — вернёмся на 1
useEffect(() => {
  if ((current - 1) * pageSize >= data.length && data.length > 0) {
    setCurrent(1);
  }
}, [data.length, pageSize, current]);

return (
  <ResponsiveTable
    columns={columns}
    dataSource={data}
    pagination={{
      current,
      pageSize,
      total: data.length,
      position: ['bottomCenter'],
      showSizeChanger: true,
      pageSizeOptions: [10, 20, 50, 100],
      onChange: (page, size) => {
        setCurrent(page);
        if (size !== pageSize) {
          setPageSize(size);
          localStorage.setItem('steeringclocks_pageSize', String(size));
        }
      },
      onShowSizeChange: (_page, size) => {
        setCurrent(1);
        setPageSize(size);
        localStorage.setItem('steeringclocks_pageSize', String(size));
      },
    }}
    rowKey="id"
    size="middle"
    scroll={{ x: 'max-content' }}
    rowClassName={(record) =>
      record.id === highlightId ? 'highlight-row' : ''
    }
    onRow={(record) => ({
      onDoubleClick: () => {
        if (highlightId === record.id) {
          localStorage.removeItem('highlightProductId');
          setHighlightId(null); // ← сразу убираем выделение без перезагрузки
        }
      },
    })}
  />
);
};

export default SteeringClocksTable;
